import { forwardRef, HTMLAttributes, useState } from "react";
import { CheckIcon } from "@radix-ui/react-icons";
import { Select } from "radix-ui";
import { Trans, useTranslation } from "react-i18next";
import { HiChevronDown } from "react-icons/hi";
import { twMerge } from "tailwind-merge";

import { SvButton } from "@/components";
import { cn } from "@/utils/tailwind.ts";

type SvDonationBlockProps = {
  className?: string;
};

type DonateBlockType = {
  amount: number;
  singleUrl: string;
  monthlyUrl: string;
};

type DonateButtonProps = DonateBlockType & {
  frequency: "single" | "monthly";
};

const DonateButton = ({
  amount,
  singleUrl,
  monthlyUrl,
  frequency,
}: DonateButtonProps) => {
  return (
    <SvButton
      color="primary"
      variant="outline"
      className="max-w-32 min-w-24 flex-1 md:max-w-28">
      <a href={frequency === "single" ? singleUrl : monthlyUrl}>
        <span>
          <Trans i18nKey="donation.amount" values={{ amount }} />
        </span>
      </a>
    </SvButton>
  );
};

type SelectItemProps = {
  children: string;
  className?: string;
  value: string;
} & HTMLAttributes<HTMLDivElement>;

const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
  ({ children, className, ...props }: SelectItemProps, forwardedRef) => {
    return (
      <Select.Item
        className={cn(
          "text-violet11 data-[highlighted]:bg-violet9 data-[disabled]:text-mauve8 data-[highlighted]:text-violet1 relative flex h-[25px] items-center rounded-[3px] pr-[35px] pl-[25px] text-[13px] leading-none select-none data-[disabled]:pointer-events-none data-[highlighted]:outline-none",
          className,
        )}
        {...props}
        ref={forwardedRef}>
        <Select.ItemText>{children}</Select.ItemText>
        <Select.ItemIndicator className="absolute left-0 inline-flex w-[25px] items-center justify-center">
          <CheckIcon />
        </Select.ItemIndicator>
      </Select.Item>
    );
  },
);
SelectItem.displayName = "SelectItem";

type FrequencySelectorProps = {
  freq: "single" | "monthly";
  setFreq: (value: "single" | "monthly") => void;
};

const FrequencySelector = ({ freq, setFreq }: FrequencySelectorProps) => {
  const { t } = useTranslation();

  return (
    <Select.Root value={freq} onValueChange={setFreq}>
      <Select.Trigger
        className="border-ind-grey-100 inline-flex h-[40px] w-full max-w-72 min-w-44 flex-1 cursor-pointer items-center justify-between gap-[5px] rounded-sm border bg-white px-4 py-3 text-sm text-[13px] leading-none font-medium focus:cursor-text focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25 min-[512px]:w-[354px] md:w-auto"
        id="donation-select-trigger">
        <Select.Value />
        <Select.Icon>
          <HiChevronDown />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          side="bottom"
          style={(function () {
            const trigger = document.getElementById("donation-select-trigger");
            if (trigger?.clientWidth) {
              return { minWidth: `${trigger.clientWidth}px` };
            }
          })()}
          className="overflow-hidden rounded-md bg-white shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]">
          <Select.Viewport>
            <SelectItem value="single">{t("donation.one_time")}</SelectItem>
            <SelectItem value="monthly">{t("donation.monthly")}</SelectItem>
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

const donationBlocks: DonateBlockType[] = [
  {
    amount: 3,
    singleUrl: "https://donate.stripe.com/14k9DLgRV4jw1CEdQS",
    monthlyUrl: "https://buy.stripe.com/fZe5nv7hl5nAa9a14d",
  },
  {
    amount: 5,
    singleUrl: "https://donate.stripe.com/eVa6rz4596rEchicMP",
    monthlyUrl: "https://buy.stripe.com/eVa2bjeJN6rEdlm28i",
  },
  {
    amount: 10,
    singleUrl: "https://donate.stripe.com/bIYg29atxaHUa9a3cg",
    monthlyUrl: "https://buy.stripe.com/28ocPXatx7vI95614f",
  },
  {
    amount: 20,
    singleUrl: "https://donate.stripe.com/00g17ffNR3fsftufZ3",
    monthlyUrl: "https://buy.stripe.com/bIYbLT3159DQbde7sE",
  },
  {
    amount: 50,
    singleUrl: "https://donate.stripe.com/aEUcPX7hl7vI5SU8wC",
    monthlyUrl: "https://buy.stripe.com/3csdU1315g2e5SU3cp",
  },
  {
    amount: 100,
    singleUrl: "https://donate.stripe.com/aEU2bj1X117k3KMbIP",
    monthlyUrl: "https://buy.stripe.com/bIYeY5bxB3fsftu8wK",
  },
];

export const SvDonationBlock = ({ className }: SvDonationBlockProps) => {
  const [frequency, setFrequency] = useState<"single" | "monthly">("single");
  const { t } = useTranslation();

  return (
    <div
      id="donation-block"
      className={twMerge(
        "flex w-full flex-col items-center justify-center bg-orange-50 px-6 py-10",
        className,
      )}>
      <div
        id="donation-block"
        className="border-secondary text-dark container flex min-h-32 flex-col justify-between gap-4 rounded-lg border bg-orange-50 px-6 py-6 text-center md:px-10"
        style={{ boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)" }}>
        <p className="text-lg font-semibold">{t("donation.title")}</p>
        <div className="flex flex-row flex-wrap items-center justify-center gap-4 md:gap-2 lg:gap-4">
          <FrequencySelector freq={frequency} setFreq={setFrequency} />
          {donationBlocks.map(block => (
            <DonateButton key={block.amount} {...block} frequency={frequency} />
          ))}
        </div>
      </div>
    </div>
  );
};
